import { useEffect, useMemo, useState } from "react";
import { useCustom } from "@refinedev/core";
import type { LucideIcon } from "lucide-react";
import {
  Lock,
  Plus,
  Minus,
  Package,
  Ambulance,
  Baby,
  BedDouble,
  Boxes,
  Flame,
  Globe,
  House,
  Lightbulb,
  Search,
  SlidersHorizontal,
  Truck,
} from "lucide-react";
import { PlusCircleSolidIcon, Button, Modal, SelectField } from "@fawkes/ui";
import { formatEuro } from "../i18n/format";
import { useBtwCodes } from "./BtwKeuze";
import { IndeelbareTabel } from "./IndeelbareTabel";

// One sellable product/tariff in the picker feed (GET /api/wizard/reference/billable-items — the
// wizard-authorized mirror of the invoice picker feed). Price/VAT may be null when there is no effective
// price rule; the operator fills the gap in the editable price field. `bron` says which catalog the row
// came from and drives the Bronnen column.
interface BillableItem {
  /** Unique across catalogs (`product:12`, `asielregel:4`): the numeric ids of the catalogs overlap. */
  key: string;
  productId: number | null;
  naam: string;
  code: string | null;
  categorie: string | null;
  prijsInclBtw: number | null;
  btwPercentage: number | null;
  bron?: string | null;
}

/** The line the operator composed in the picker (Jmix FactuurRegelDialog.GekozenRegel, trimmed). */
export interface GekozenProductRegel {
  omschrijving: string;
  aantal: number;
  prijsInclBtw: number;
  /**
   * Het BTW-percentage van de regel: dat van het gekozen product/tarief, of — heeft dat er geen — het
   * percentage dat de gebruiker in de kiezer koos. Altijd gezet: BTW is verplicht (0% mag), en de
   * factuur mag er niet meer stilletjes 21% van maken. De wizards vouwen de regel samen tot één bedrag
   * en negeren het; het factuurscherm slaat het op de regel op.
   */
  btwPercentage: number;
}

/** Parse a euro/count amount from a free-text field (comma or dot decimal); null when blank/NaN. */
function parseDecimal(value: string): number | null {
  const s = value.trim().replace(/\s/g, "").replace(",", ".");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Een bron links in de kiezer; {@code key} is de {@code bron} van de regels in de feed, leeg is "alle". */
export interface PickerBron {
  key: string;
  labelKey: string;
  icon: LucideIcon;
}

/** The eight catalogs of the design, in order; `key` matches the feed's `bron` value. */
const BRONNEN: readonly PickerBron[] = [
  { key: "", labelKey: "picker.bron.alle", icon: Globe },
  { key: "Producten & Diensten", labelKey: "picker.bron.producten", icon: Boxes },
  { key: "Pension tarieven", labelKey: "picker.bron.pension", icon: BedDouble },
  { key: "Crèche tarieven", labelKey: "picker.bron.creche", icon: Baby },
  { key: "Asiel tarieven", labelKey: "picker.bron.asiel", icon: House },
  { key: "Dierenambulance tarieven", labelKey: "picker.bron.ambulance", icon: Ambulance },
  { key: "Crematie tarieven", labelKey: "picker.bron.crematie", icon: Flame },
  { key: "Crematie vervoer", labelKey: "picker.bron.vervoer", icon: Truck },
] as const;

const PER_PAGINA = [10, 25, 50] as const;
type Sortering = "meest" | "naam" | "naamAf" | "prijsOp" | "prijsAf";

/**
 * "Product of tarief toevoegen" — ONE picker for the three places a cost line is composed: the intake
 * wizard's Afstandskosten step, the release wizard's Adoptie/Opvangkosten step and "Regel toevoegen" on
 * the invoice screen (maintainer design 2026-08-17, replacing the plain search list).
 *
 * <p>Left: the catalogs (Bronnen) with a per-source count. Middle: search, category filter, sorting and
 * the radio table (Omschrijving · Categorie · Prijs incl. btw · Btw · Bron) with paging. Right: the
 * "Geselecteerde regel" panel where the line is composed — aantal × prijs, an editable omschrijving and
 * the running line total. Toevoegen collapses that into one line via {@code onPick}, exactly how the
 * Jmix wizards consumed the dialog (GekozenRegel → CostLine).
 *
 * <p>A source with no rows in the feed says so rather than looking mysteriously empty: only the product
 * catalog is served today, and the tariff catalogs join through the same {@code bron} field without this
 * component changing again.
 */
/** De naam van een bron zoals hij links in de lijst staat. */
function bronLabel(
  bron: string | null | undefined,
  t: (k: string) => string,
  lijst: readonly PickerBron[] = BRONNEN,
): string {
  const gevonden = lijst.find((b) => b.key === (bron ?? ""));
  return gevonden ? t(gevonden.labelKey) : (bron ?? "");
}

/**
 * De bron van een regel als icoon. In de kolom BRON paste de volledige bronnaam niet — de tabel
 * ging ervan dwars scrollen en de naam werd afgekapt. Het icoon is hetzelfde als links in de
 * bronnenlijst, dus je herkent het zonder te lezen; de naam staat in de tooltip.
 */
function BronIcoon({
  bron,
  titel,
  lijst = BRONNEN,
}: {
  bron: string | null | undefined;
  titel: string;
  lijst?: readonly PickerBron[];
}) {
  const gevonden = lijst.find((b) => b.key === (bron ?? ""));
  const Icon = gevonden?.icon ?? Package;
  return (
    <span className="fawkes-picker__bron-merk" title={titel} role="img" aria-label={titel}>
      <Icon size={16} aria-hidden="true" />
    </span>
  );
}

export function ProductTariefPicker({
  open,
  onClose,
  onPick,
  t,
  locale,
  feedPath = "wizard/reference/billable-items",
  bronnen = BRONNEN,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (regel: GekozenProductRegel) => void;
  t: (k: string, options?: { args?: unknown[] }) => string;
  locale: string;
  /**
   * Which catalog feed to search. Two endpoints serve the identical payload behind different
   * authorities: the wizard mirror (default — desk operators have no FACTUUR_VIEW) and
   * {@code facturen/billable-items} for the invoice screen. Relative; the dataProvider adds /api.
   */
  feedPath?: string;
  /**
   * De bronnen links, als een scherm er minder of andere toont dan de acht van de algemene kiezer — het
   * pension toont bijvoorbeeld geen asiel- of crematietarieven, maar wel wat eerder bij het pension gebruikt
   * is. Standaard de acht.
   */
  bronnen?: readonly PickerBron[];
}) {
  const [q, setQ] = useState("");
  const [bron, setBron] = useState("");
  const [categorie, setCategorie] = useState("");
  const [sortering, setSortering] = useState<Sortering>("meest");
  const [perPagina, setPerPagina] = useState<number>(10);
  const [pagina, setPagina] = useState(1);
  const [selected, setSelected] = useState<BillableItem | null>(null);
  const [aantal, setAantal] = useState("1");
  const [prijs, setPrijs] = useState("");
  const [omschrijving, setOmschrijving] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Heeft het gekozen product of tarief geen BTW, dan kiest de gebruiker hem hier. Een stille 21% was
  // precies de fout: een vrijgesteld tarief kwam zo met 21% op de factuur.
  const [btwKeuze, setBtwKeuze] = useState("");
  const btwKiezen = selected != null && selected.btwPercentage == null;
  const btwCodes = useBtwCodes(open && btwKiezen);
  // Per percentage één keuze, hoogste eerst (21% · 9% · 0%): de factuurregel slaat een percentage op,
  // dus twee codes met hetzelfde percentage zijn voor deze keuze dezelfde.
  const btwPercentages = useMemo(
    () => [...new Set(btwCodes.map((c) => c.percentage).filter((p): p is number => p != null))]
      .sort((a, b) => b - a),
    [btwCodes],
  );

  const url = `${feedPath}${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`;
  const { data, isLoading, isError } = useCustom<BillableItem[]>({
    url,
    method: "get",
    queryOptions: { enabled: open },
  });
  const raw = data?.data;
  const items: BillableItem[] = Array.isArray(raw) ? raw : [];

  // Any filter change starts at page one; otherwise you land on an empty page 4 of a 3-row result.
  useEffect(() => { setPagina(1); }, [q, bron, categorie, sortering, perPagina]);

  // De categorieën van de gekozen bron, niet van alles: een asieltarief heeft geen
  // crematiecategorie, dus die keuzes leveren gegarandeerd nul regels op.
  const categorieen = useMemo(
    () => [...new Set(items.filter((r) => !bron || (r.bron ?? "") === bron)
      .map((r) => r.categorie ?? "").filter(Boolean))].sort(),
    [items, bron],
  );
  // Van bron wisselen laat anders een categorie staan die daar niet bestaat — je krijgt dan een
  // lege tabel zonder dat te zien is waardoor.
  useEffect(() => {
    if (categorie && !categorieen.includes(categorie)) setCategorie("");
  }, [categorieen, categorie]);

  const gefilterd = useMemo(() => {
    const lijst = items.filter((r) =>
      (!bron || (r.bron ?? "") === bron) && (!categorie || (r.categorie ?? "") === categorie));
    const prijsVan = (r: BillableItem) => r.prijsInclBtw ?? 0;
    const opNaam = (a: BillableItem, b: BillableItem) => a.naam.localeCompare(b.naam, locale);
    if (sortering === "prijsOp") lijst.sort((a, b) => prijsVan(a) - prijsVan(b));
    else if (sortering === "prijsAf") lijst.sort((a, b) => prijsVan(b) - prijsVan(a));
    else if (sortering === "naam") lijst.sort(opNaam);
    else if (sortering === "naamAf") lijst.sort((a, b) => opNaam(b, a));
    // "Meest gebruikt": eerst op bron, daarbinnen op naam — de volgorde van de bron.
    else lijst.sort((a, b) => (a.bron ?? "").localeCompare(b.bron ?? "", locale) || opNaam(a, b));
    return lijst;
  }, [items, bron, categorie, sortering, locale]);

  const paginas = Math.max(1, Math.ceil(gefilterd.length / perPagina));
  const zichtbaar = gefilterd.slice((pagina - 1) * perPagina, pagina * perPagina);

  function reset() {
    setQ("");
    setBron("");
    setCategorie("");
    setSortering("meest");
    setPagina(1);
    setSelected(null);
    setAantal("1");
    setPrijs("");
    setOmschrijving("");
    setBtwKeuze("");
    setError(null);
  }

  function select(item: BillableItem) {
    setSelected(item);
    setAantal("1");
    setPrijs(item.prijsInclBtw != null ? item.prijsInclBtw.toFixed(2) : "");
    setOmschrijving(item.naam);
    setBtwKeuze("");
    setError(null);
  }

  const aantalNum = parseDecimal(aantal);
  const prijsNum = parseDecimal(prijs);
  const totaal = aantalNum != null && prijsNum != null ? aantalNum * prijsNum : null;

  function submit() {
    // Jmix FactuurRegelDialog.save() validation, verbatim message per failing field.
    if (aantalNum == null || aantalNum <= 0) {
      setError(t("wizard.productPicker.aantalInvalid"));
      return;
    }
    if (prijsNum == null) {
      setError(t("wizard.productPicker.prijsInvalid"));
      return;
    }
    if (!omschrijving.trim()) {
      setError(t("wizard.productPicker.omschrijvingInvalid"));
      return;
    }
    const btwPercentage = selected?.btwPercentage ?? (btwKeuze === "" ? null : Number(btwKeuze));
    if (btwPercentage == null) {
      setError(t("picker.btw.verplicht"));
      return;
    }
    onPick({
      omschrijving: omschrijving.trim(),
      aantal: aantalNum,
      prijsInclBtw: prijsNum,
      btwPercentage,
    });
    reset();
    onClose();
  }

  const sluit = () => { reset(); onClose(); };

  return (
    <Modal
      open={open}
      title={t("wizard.productPicker.title")}
      onClose={sluit}
      size="wide"
      footer={
        <>
          <Button variant="secondary" onClick={sluit}>{t("common.cancel")}</Button>
          <Button variant="primary" onClick={submit} disabled={selected == null || (btwKiezen && btwKeuze === "")}>
            <PlusCircleSolidIcon width={18} height={18} /> {t("wizard.productPicker.add")}
          </Button>
        </>
      }
    >
      <p className="fawkes-picker__intro">{t("wizard.productPicker.subtitle")}</p>
      <div className="fawkes-picker">
        <aside className="fawkes-picker__bronnen" aria-label={t("picker.bronnen")}>
          <h3>{t("picker.bronnen")}</h3>
          {bronnen.map(({ key, labelKey, icon: Icon }) => {
            const aantalInBron = key ? items.filter((r) => (r.bron ?? "") === key).length : items.length;
            return (
              <button
                key={key || "alle"}
                type="button"
                className={`fawkes-picker__bron${bron === key ? " is-active" : ""}`}
                aria-pressed={bron === key}
                onClick={() => setBron(key)}
              >
                <span className="fawkes-picker__bron-icon" aria-hidden="true"><Icon size={16} /></span>
                <span className="fawkes-picker__bron-label">{t(labelKey)}</span>
                {aantalInBron > 0 && <span className="fawkes-picker__bron-count">{aantalInBron}</span>}
              </button>
            );
          })}
          <div className="fawkes-picker__hint">
            <Lightbulb size={16} aria-hidden="true" />
            <div>
              <strong>{t("picker.hint.title")}</strong>
              <span>{t("picker.hint.body")}</span>
            </div>
          </div>
        </aside>

        <section className="fawkes-picker__lijst">
          <label className="fawkes-picker__search">
            <Search size={16} aria-hidden="true" />
            <input
              value={q}
              placeholder={t("wizard.productPicker.search")}
              aria-label={t("wizard.productPicker.search")}
              onChange={(e) => setQ(e.target.value)}
              autoFocus
            />
          </label>

          <div className="fawkes-picker__controls">
            <SelectField
              label={t("picker.categorie")}
              value={categorie}
              onChange={(e) => setCategorie(e.target.value)}
              options={[{ value: "", label: t("picker.categorie.alle") },
                ...categorieen.map((c) => ({ value: c, label: c }))]}
            />
            <SelectField
              label={t("picker.sorteren")}
              value={sortering}
              onChange={(e) => setSortering(e.target.value as Sortering)}
              options={[
                { value: "meest", label: t("picker.sorteren.meest") },
                { value: "naam", label: t("picker.sorteren.naam") },
                { value: "naamAf", label: t("picker.sorteren.naamAf") },
                { value: "prijsOp", label: t("picker.sorteren.prijsOp") },
                { value: "prijsAf", label: t("picker.sorteren.prijsAf") },
              ]}
            />
          </div>

          <div className="fawkes-picker__tablewrap">
            <IndeelbareTabel
              tabelKey="product-tarief-kiezer"
              columns={[
                {
                  // Het rondje blijft waar het staat: het hoort bij de rij en niet bij de gegevens.
                  key: "keuze", header: "", vast: true,
                  cell: (item) => (
                    <input
                      type="radio"
                      name="fawkes-picker-regel"
                      checked={selected?.key === item.key}
                      onChange={() => select(item)}
                      aria-label={item.naam}
                    />
                  ),
                },
                {
                  key: "omschrijving", header: t("picker.kolom.omschrijving"),
                  cell: (item) => (
                    <span className="fawkes-picker__naam">
                      <span className="fawkes-picker__naam-tekst">{item.naam}</span>
                      {item.code && <span className="fawkes-picker__code">{item.code}</span>}
                    </span>
                  ),
                },
                { key: "categorie", header: t("picker.kolom.categorie"), cell: (item) => item.categorie ?? "—" },
                {
                  key: "prijs", header: t("picker.kolom.prijs"), align: "right",
                  cell: (item) => <span className="tabular">{formatEuro(item.prijsInclBtw, locale)}</span>,
                },
                {
                  key: "btw", header: t("picker.kolom.btw"), align: "right",
                  cell: (item) => (
                    <span className="tabular">{item.btwPercentage != null ? `${item.btwPercentage}%` : "—"}</span>
                  ),
                },
                {
                  key: "bron", header: t("picker.kolom.bron"),
                  cell: (item) => (
                    <span className="fawkes-picker__bronkolom">
                      <BronIcoon bron={item.bron} titel={bronLabel(item.bron, t, bronnen)} lijst={bronnen} />
                    </span>
                  ),
                },
              ]}
              rows={zichtbaar}
              getRowKey={(item) => item.key}
              onRowClick={select}
              rowClassName={(item) => (selected?.key === item.key ? "is-selected" : undefined)}
              empty=""
            />
            {gefilterd.length === 0 && (
              <p className="fawkes-picker__state">
                {isLoading
                  ? t("common.loading")
                  : isError
                    ? t("picker.laadFout")
                    : bron
                      ? t("picker.leegBron")
                      : t("picker.geenRegels")}
              </p>
            )}
          </div>

          <div className="fawkes-picker__pager">
            <span>
              {gefilterd.length === 0
                ? t("picker.geenResultaten")
                : t("picker.resultaten", {
                  args: [
                    String((pagina - 1) * perPagina + 1),
                    String(Math.min(pagina * perPagina, gefilterd.length)),
                    String(gefilterd.length),
                  ],
                })}
            </span>
            <div className="fawkes-picker__pages">
              <button type="button" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>‹</button>
              {Array.from({ length: Math.min(5, paginas) }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  aria-current={n === pagina}
                  className={n === pagina ? "is-active" : undefined}
                  onClick={() => setPagina(n)}
                >
                  {n}
                </button>
              ))}
              <button type="button" disabled={pagina >= paginas} onClick={() => setPagina((p) => p + 1)}>›</button>
            </div>
            <SelectField
              label=""
              value={String(perPagina)}
              onChange={(e) => setPerPagina(Number(e.target.value))}
              options={PER_PAGINA.map((n) => ({
                value: String(n), label: t("picker.perPagina", { args: [String(n)] }),
              }))}
            />
          </div>
          <p className="fawkes-picker__tip">
            <SlidersHorizontal size={14} aria-hidden="true" /> {t("picker.tip")}
          </p>
        </section>

        <aside className="fawkes-picker__gekozen" aria-label={t("wizard.productPicker.selectedTitle")}>
          <h3>{t("wizard.productPicker.selectedTitle")}</h3>
          {selected == null ? (
            <p className="fawkes-picker__gekozen-leeg">{t("wizard.productPicker.selectEmpty")}</p>
          ) : (
            <>
              <div className="fawkes-picker__kaart">
                <span className="fawkes-picker__kaart-icoon" aria-hidden="true">
                  <BronIcoon bron={selected.bron} titel={bronLabel(selected.bron, t, bronnen)} lijst={bronnen} />
                </span>
                <span className="fawkes-picker__kaart-tekst">
                  <span className="fawkes-picker__gekozen-naam">{selected.naam}</span>
                  {selected.code && <span className="fawkes-picker__kaart-code">{selected.code}</span>}
                  <span className="fawkes-picker__kaart-badge">{bronLabel(selected.bron, t, bronnen)}</span>
                </span>
              </div>

              <div className="fawkes-picker__inforij">
                <span>{t("picker.info.bron")}</span>
                <span>{bronLabel(selected.bron, t, bronnen)}</span>
              </div>
              <div className="fawkes-picker__inforij">
                <span>{t("picker.info.standaardprijs")}</span>
                <span>{selected.prijsInclBtw != null ? formatEuro(selected.prijsInclBtw, locale) : "—"}</span>
              </div>
              {btwKiezen ? (
                <div className="fawkes-picker__veld">
                  <SelectField
                    label={t("picker.btw.label")}
                    value={btwKeuze}
                    required
                    aria-required="true"
                    onChange={(e) => { setBtwKeuze(e.target.value); setError(null); }}
                    options={[
                      { value: "", label: t("btw.kies") },
                      ...btwPercentages.map((p) => ({ value: String(p), label: `${p}%` })),
                    ]}
                  />
                  <span className="fawkes-picker__veld-help">{t("picker.btw.help")}</span>
                </div>
              ) : (
                <div className="fawkes-picker__inforij">
                  <span>{t("picker.info.btw")}</span>
                  <span>{`${selected.btwPercentage}%`}</span>
                </div>
              )}

              {/* De bron zet het aantal in een stepper: je verhoogt meestal met één tegelijk. */}
              <div className="fawkes-picker__veld">
                <span className="fawkes-picker__veld-label">{t("wizard.productPicker.aantal")}</span>
                <div className="fawkes-picker__stepper">
                  <button
                    type="button"
                    aria-label={t("picker.aantalMinder")}
                    onClick={() => setAantal(String(Math.max(0, (parseDecimal(aantal) ?? 0) - 1)))}
                  >
                    <Minus size={14} aria-hidden="true" />
                  </button>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={aantal}
                    aria-label={t("wizard.productPicker.aantal")}
                    onChange={(e) => setAantal(e.target.value)}
                  />
                  <button
                    type="button"
                    aria-label={t("picker.aantalMeer")}
                    onClick={() => setAantal(String((parseDecimal(aantal) ?? 0) + 1))}
                  >
                    <Plus size={14} aria-hidden="true" />
                  </button>
                </div>
              </div>

              <div className="fawkes-picker__veld">
                <span className="fawkes-picker__veld-label">{t("wizard.productPicker.prijs")}</span>
                <div className="fawkes-picker__prijsveld">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={prijs}
                    aria-label={t("wizard.productPicker.prijs")}
                    placeholder={t("inboeken.costs.amount.placeholder")}
                    onChange={(e) => setPrijs(e.target.value)}
                  />
                  {/* Het slotje uit de bron: het zegt dat dit veld van jou is, niet van de catalogus. */}
                  <Lock size={14} aria-hidden="true" />
                </div>
                <span className="fawkes-picker__veld-help">{t("wizard.productPicker.prijsHelp")}</span>
              </div>

              <div className="fawkes-picker__veld">
                <span className="fawkes-picker__veld-label">{t("wizard.productPicker.omschrijving")}</span>
                <textarea
                  className="fawkes-picker__omschrijving"
                  value={omschrijving}
                  maxLength={255}
                  aria-label={t("wizard.productPicker.omschrijving")}
                  onChange={(e) => setOmschrijving(e.target.value)}
                />
                <span className="fawkes-picker__veld-help">
                  {t("picker.tekens", { args: [String(omschrijving.length)] })}
                </span>
              </div>

              <div className="fawkes-picker__gekozen-totaal">
                <span className="fawkes-picker__totaal-tekst">
                  {t("wizard.productPicker.totaal")}
                  <span className="fawkes-picker__totaal-sub">
                    {t("picker.totaalOpbouw", {
                      args: [aantal || "0", prijsNum != null ? formatEuro(prijsNum, locale) : "—"],
                    })}
                  </span>
                </span>
                <strong>{totaal != null ? formatEuro(totaal, locale) : "—"}</strong>
              </div>
              {error && (
                <span className="fawkes-inboeken__field-error" role="alert">{error}</span>
              )}
            </>
          )}
        </aside>
      </div>
    </Modal>
  );
}
